import { CommonModule } from '@angular/common';
import { Component, input, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { MaterialModule } from 'src/app/material.module';
import {
  firstValueFrom
} from 'rxjs';
import { tasksServices } from 'src/app/services/tasks.service';
import { HtmlEditorComponent } from 'src/app/shared/html-editor/html-editor.component';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { SelectComponent } from 'src/app/shared/select/select.component';
import { generalService } from 'src/app/services/general-info';
import { userDataService } from 'src/app/services/userDataService';

interface Field {
  fieldName: string;
  fieldType: string;
  isRequired: boolean;
  isDefault?: boolean;
  isLeadField?: boolean;
  showInScheduler?: boolean;
  options?: string;
  prefillValue?: string;
  key?: string;
  order?: number;
}


@Component({
  selector: 'app-meeting-details',
  standalone: true,
  imports: [MaterialModule, CommonModule, ReactiveFormsModule, HtmlEditorComponent, DragDropModule, SelectComponent],
  templateUrl: './meeting-details.component.html',
  styleUrl: './meeting-details.component.scss'
})
export class MeetingDetailsComponent implements OnInit {
  // Reactive form declaration
  meetingForm: FormGroup;
  fieldTypes = [
    { value: 'text', label: 'Text field' },
    { value: 'textarea', label: 'Text area' },
    { value: 'select', label: 'Dropdown' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'email', label: 'Email' },
    { value: 'tel', label: 'Phone' },
    { value: 'date', label: 'Date' }
  ];

  activityTypes: any[] = [];
  sources: any[] = [];
  sourceFormControl = new FormControl([]);
  
  quillModule = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'], // Basic formatting
        ['link'], // Media options
        [{ list: 'ordered' }, { list: 'bullet' }], // Lists
        ['clean'], // Clear formatting
        ['speechToText'], // Add speech-to-text button
      ],
      handlers: {
        speechToText: () => {}, // Placeholder for now, managed in HtmlEditorComponent
      },
    },
  };

  meetingFormData = input<any>();
  isEditMode: boolean = false;
  showPrefillInputs: boolean = false;
  isAvailibility = input<boolean>(false);
  
  // Default field types
  defaultFields: Field[] = [
    { fieldName: 'Full name', fieldType: 'text', isRequired: true, isDefault: true, key: 'name', order: 0 },
    { fieldName: 'Email', fieldType: 'email', isRequired: true, isDefault: true, key: 'email', order: 1 },
    { fieldName: 'Phone', fieldType: 'tel', isRequired: false, isDefault: true, key: 'phone', order: 2 }
  ];

  // Lead fields - including name, email, phone as lead fields
  leadFields: Field[] = [
    { fieldName: 'First name', fieldType: 'text', isRequired: true, isLeadField: true, showInScheduler: true, key: 'first_name', order: 0 },
    { fieldName: 'Last name', fieldType: 'text', isRequired: true, isLeadField: true, showInScheduler: true, key: 'last_name', order: 1 },
    { fieldName: 'Email', fieldType: 'email', isRequired: true, isLeadField: true, showInScheduler: true, key: 'email', order: 2 },
    { fieldName: 'Phone', fieldType: 'tel', isRequired: true, isLeadField: true, showInScheduler: true, key: 'phone', order: 3 },
    { fieldName: 'Campus', fieldType: 'select', isRequired: true, isLeadField: true, showInScheduler: true, key: 'campus', options: 'Campus A, Campus B, Campus C', order: 4 },
    { fieldName: 'Program', fieldType: 'select', isRequired: true, isLeadField: true, showInScheduler: true, key: 'program', options: 'Program X, Program Y, Program Z', order: 5 },
    { fieldName: 'Zip code', fieldType: 'text', isRequired: false, isLeadField: true, showInScheduler: false, key: 'zip_code', order: 7 },
    // { fieldName: 'Postal code', fieldType: 'text', isRequired: false, isLeadField: true, showInScheduler: false, key: 'postal_code', order: 6 },
    { fieldName: 'State', fieldType: 'select', isRequired: false, isLeadField: true, showInScheduler: false, key: 'state', options: 'AL, AK, AZ, AR, CA, CO, CT, DE, FL, GA, HI, ID, IL, IN, IA, KS, KY, LA, ME, MD, MA, MI, MN, MS, MO, MT, NE, NV, NH, NJ, NM, NY, NC, ND, OH, OK, OR, PA, RI, SC, SD, TN, TX, UT, VT, VA, WA, WV, WI, WY', order: 7 },
    { fieldName: 'US Citizen', fieldType: 'checkbox', isRequired: false, isLeadField: true, showInScheduler: false, key: 'us_citizen', order: 8 },
    { fieldName: 'Marital status', fieldType: 'select', isRequired: false, isLeadField: true, showInScheduler: false, key: 'marital_status', options: 'Single, Married, Divorced, Widowed', order: 9 },
    { fieldName: 'High school attended', fieldType: 'text', isRequired: false, isLeadField: true, showInScheduler: false, key: 'high_school_attended', order: 10 },
    { fieldName: 'Preferred contact method', fieldType: 'select', isRequired: false, isLeadField: true, showInScheduler: false, key: 'preferred_contact_method', options: 'Sms, Email, Phone', order: 11 },
    { fieldName: 'Birth date', fieldType: 'date', isRequired: false, isLeadField: true, showInScheduler: false, key: 'birth_date', order: 12 },
    { fieldName: 'Gender', fieldType: 'select', isRequired: false, isLeadField: true, showInScheduler: false, key: 'gender', options: 'Male, Female, Other, Prefer not to say', order: 13 },
    { fieldName: 'High school graduation year', fieldType: 'number', isRequired: false, isLeadField: true, showInScheduler: false, key: 'highSchoolYearGraduation', order: 14 },
  ];

  userId: any;
  hasGoogleMeet: boolean = false;
  hasZoomMeet:boolean = false;
  user:any;
  private lastLeadFieldCount: number = -1;

  constructor(
    private fb: FormBuilder, 
    private taskService: tasksServices,
    private cdr: ChangeDetectorRef,
    private generalService: generalService,
    private userDataService:userDataService
  ) {
    this.createForm();
  }

  async ngOnInit() {
    this.user = await this.userDataService.getUserData();
    
    // ✅ Added: set user-dependent properties here
    this.userId = this.user?.id ?? null;
    this.hasGoogleMeet = !!this.user?.GoogleTokens?.find(row => row.service === "MEET");
    this.hasZoomMeet = !!this.user?.GoogleTokens?.find(row => row.service === "ZOOM");

    const activitiesTypes = await firstValueFrom(this.loadActivityTypes());
    this.activityTypes = activitiesTypes.data;
    
    await this.loadSourceData();
    
    if (this.user) {
      this.meetingForm.patchValue({
        companyName: this.user.Company?.name,
        type_id: this.activityTypes.length ? this.activityTypes[0].id : null
      });
    }

    if (this.meetingFormData()) {
      this.isEditMode = true;
      this.populateFormData();
    } else {
      this.initializeDefaultFields();
    }

    this.meetingForm.get('prefillNameEmail')?.valueChanges.subscribe(value => {
      this.showPrefillInputs = value;
      const fieldArray = this.meetingForm.get('fields') as FormArray;
      if (fieldArray) {
        fieldArray.controls.forEach(control => {
          if (control.get('isDefault')?.value) {
            control.get('showPrefill')?.setValue(value);
          }
        });
      }
    });

    this.meetingForm.get('createLeadMode')?.valueChanges.subscribe(value => {
      if (value === 'lead-and-meeting') {
        this.removeDefaultFields();
        this.initializeLeadFields();
        this.meetingForm.get('prefillNameEmail')?.setValue(false);
      } else {
        this.removeLeadFields();
        this.initializeDefaultFields();
      }
      this.cdr.detectChanges();
    });
}

  async loadSourceData() {
    try {
      this.sources = await firstValueFrom(this.generalService.get({}, 'source'));
    } catch (error) {
      console.error('Failed to load sources:', error);
      this.sources = [];
    }
  }

  onSourceChange(selection: string[]): void {
    // Since we only allow single selection, take the first item
    const selectedSourceId = selection?.length > 0 ? selection[0] : null;
    this.meetingForm.get('sourceId')?.setValue(selectedSourceId);
  }

  loadActivityTypes() {
    return this.taskService.getActivityTypes();
  }

  createForm() {
    this.meetingForm = this.fb.group({
      meetingName: ['Meeting', Validators.required],
      meetingLocation: [''],
      videoCallOption: ['no-video-call'],
      meetingDescription: [''],
      companyName: [''],
      createLeadMode: ['meeting-only'],
      sourceId: [null],
      prefillNameEmail: [false],
      footerText: [''],
      type_id: [null, Validators.required],
      activityNote: [''],
      fields: this.fb.array([])
    });
}

  initializeDefaultFields() {
    // Create default fields from the predefined list
    this.defaultFields.forEach((field, index) => {
      this.addFieldWithKey(
        field.fieldName,
        field.fieldType,
        field.isRequired,
        true, // isDefault
        false, // isLeadField
        true, // showInScheduler - default fields should be visible
        '', // options
        '', // prefillValue
        index, // order
        field.key || null // key
      );
    });
  }

  removeDefaultFields() {
    // Remove all default fields from the form
    const fieldArray = this.meetingForm.get('fields') as FormArray;
    for (let i = fieldArray.length - 1; i >= 0; i--) {
      const field = fieldArray.at(i);
      if (field.get('isDefault')?.value === true) {
        fieldArray.removeAt(i);
      }
    }
  }

  initializeLeadFields() {
    // Check if lead fields are already added
    const existingLeadFields = this.getLeadFields();
    if (existingLeadFields.length > 0) {
      return;
    }
    
    // Create lead fields from the predefined list
    this.leadFields.forEach((field, index) => {
      this.addFieldWithKey(
        field.fieldName,
        field.fieldType,
        field.isRequired,
        false, // isDefault
        true, // isLeadField
        field.showInScheduler !== undefined ? field.showInScheduler : true,
        field.options || '',
        '', // prefillValue
        field.order || index, // order
        field.key || null // key
      );
    });
    
    // Force change detection after adding lead fields
    this.cdr.detectChanges();
  }

  updateFieldValidationState(fieldControl: FormGroup): void {
    const isLeadField = fieldControl.get('isLeadField')?.value;
    const showInScheduler = fieldControl.get('showInScheduler')?.value;
    const isRequiredControl = fieldControl.get('isRequired');
    const isLeadMode = this.meetingForm.get('createLeadMode')?.value === 'lead-and-meeting';
    
    if (isLeadField && isLeadMode && isRequiredControl) {
      if (!showInScheduler) {
        isRequiredControl.setValue(false);
        isRequiredControl.disable();
      } else {
        isRequiredControl.enable();
      }
    }
  }

  removeLeadFields() {
    // Remove all lead fields from the form
    const fieldArray = this.meetingForm.get('fields') as FormArray;
    for (let i = fieldArray.length - 1; i >= 0; i--) {
      const field = fieldArray.at(i);
      if (field.get('isLeadField')?.value === true) {
        fieldArray.removeAt(i);
      }
    }
  }

  populateFormData() {
    const data = this.meetingFormData();
    // Determine if prefill should be enabled
    // Check if any field has a non-empty prefillValue to know if prefill was enabled
    const hasPrefillValues = data.fields?.some(field => field.prefillValue) || false;
    
    // Determine if this is a lead meeting based on createLead field from data
    const isLeadMeeting = data.createLeadMode === 'lead-and-meeting' || data.createLead === true;
    // Patch basic form data
    this.meetingForm.patchValue({
      meetingName: data.meetingName || 'Meeting',
      meetingLocation: data.meetingLocation || '',
      videoCallOption: data.videoCallLink || 'no-video-call',
      meetingDescription: data.meetingDescription || '',
      companyName: data.companyName || '',
      createLeadMode: isLeadMeeting ? 'lead-and-meeting' : 'meeting-only',
      prefillNameEmail: hasPrefillValues && !isLeadMeeting, // Only enable prefill in meeting-only mode
      footerText: data.footerText || '',
      type_id: data.type_id || (this.activityTypes.length ? this.activityTypes[0].id : null),
      activityNote: data.activityNote || '',
      sourceId: data.source_id?.toString() || null // Set the source if available
    });
    // Set source form control value if source exists
    if (data.source_id) {
      this.sourceFormControl.setValue([data.source_id.toString()]);
    }
    
    // Set prefill visibility based on calculated value
    this.showPrefillInputs = hasPrefillValues && !isLeadMeeting;
    
    // Clear existing fields first using the safe method
    const fieldArray = this.meetingForm.get('fields') as FormArray;
    fieldArray.clear(); // ✅ Use .clear() instead of while loop
    
    // Instead of manually adding fields one by one, we'll use our specialized methods
    if (isLeadMeeting) {
      // First add all lead fields with default values
      this.initializeLeadFields();
      
      // Then, if we have data, update the fields with the saved values
      if (data.fields && data.fields.length > 0) {
        // Sort the data fields by order to maintain proper sequence
        const sortedFields = [...data.fields].sort((a, b) => (a.order || 0) - (b.order || 0));
        
        // Get the fields array
        const updatedFieldArray = this.meetingForm.get('fields') as FormArray;
        
        sortedFields.forEach((dataField: any) => {
          // Get field name (handling both frontend and backend field naming)
          const fieldName = dataField.label || dataField.fieldName;
          
          // Find the matching field in our form
          for (let i = 0; i < updatedFieldArray.length; i++) {
            const fieldControl = updatedFieldArray.at(i);
            const controlFieldName = fieldControl.get('fieldName')?.value;
            const controlFieldKey = fieldControl.get('key')?.value;
            
            // Match by name OR key for better reliability
            const isMatch = controlFieldName === fieldName || 
                           (dataField.key && controlFieldKey && controlFieldKey === dataField.key);
            
            // If we find a matching field, update its values
            if (isMatch) {
              // Update the field with exact values from data
              fieldControl.patchValue({
                isRequired: dataField.isRequired,
                showInScheduler: dataField.showInScheduler,
                options: dataField.options || '',
                prefillValue: dataField.prefillValue || '',
                order: dataField.order || i
              });
              break;
            }
          }
        });
        
        // Now add any custom fields (non-lead, non-default)
        sortedFields.forEach((dataField: any, index: number) => {
          const fieldName = dataField.label || dataField.fieldName;
          const fieldKey = dataField.key;
          
          // Check if this is a standard lead field by name OR key
          const isStandardLeadField = this.leadFields.some(
            leadField => leadField.fieldName.toLowerCase() === fieldName.toLowerCase() ||
                        (fieldKey && leadField.key === fieldKey)
          );
          
          // If not a standard lead field and not a default field, add it as custom
          if (!isStandardLeadField && !this.isDefaultFieldByName(fieldName) && 
              (!dataField.isLeadField || (dataField.isLeadField && !isStandardLeadField))) {
            this.addFieldWithKey(
              fieldName,
              dataField.type || dataField.fieldType || 'text',
              dataField.isRequired,
              false, // isDefault
              false, // isLeadField
              dataField.showInScheduler, 
              dataField.options || '',
              dataField.prefillValue || '',
              dataField.order || (this.fields.length + index),
              dataField.key || null
            );
          }
        });
      }
    } else {
      // Meeting only mode
      // First add all default fields with default values
      this.initializeDefaultFields();
      
      // Then update with saved values if available
      if (data.fields && data.fields.length > 0) {
        // Sort the data fields by order to maintain proper sequence
        const sortedFields = [...data.fields].sort((a, b) => (a.order || 0) - (b.order || 0));
        const updatedFieldArray = this.meetingForm.get('fields') as FormArray;
        
        sortedFields.forEach((dataField: any) => {
          const fieldName = dataField.label || dataField.fieldName;
          const fieldKey = dataField.key;
          const isDefault = this.isDefaultFieldByName(fieldName);
          
          if (isDefault) {
            // Find the matching default field and update it by name OR key
            for (let i = 0; i < updatedFieldArray.length; i++) {
              const fieldControl = updatedFieldArray.at(i);
              const controlFieldName = fieldControl.get('fieldName')?.value;
              const controlFieldKey = fieldControl.get('key')?.value;
              
              // Match by name OR key for better reliability
              const isMatch = controlFieldName === fieldName || 
                             (fieldKey && controlFieldKey && controlFieldKey === fieldKey);
              
              if (isMatch) {
                fieldControl.patchValue({
                  isRequired: dataField.isRequired,
                  options: dataField.options || '',
                  prefillValue: dataField.prefillValue || '',
                  showPrefill: this.showPrefillInputs,
                  order: dataField.order || i,
                  key: dataField.key || fieldControl.get('key')?.value
                });
                break;
              }
            }
          } else if (!this.isLeadFieldByName(fieldName)) {
            // This is a custom field, add it
            this.addFieldWithKey(
              fieldName,
              dataField.type || dataField.fieldType || 'text',
              dataField.isRequired,
              false, // isDefault
              false, // isLeadField
              dataField.showInScheduler,
              dataField.options || '',
              dataField.prefillValue || '',
              dataField.order || this.fields.length,
              dataField.key || null
            );
          }
        });
      }
    }

    this.fields.controls.forEach(control => {
      if (control instanceof FormGroup) {
        this.setupFieldValidationLogic(control);
        this.updateFieldValidationState(control);
      }
    });
    
    // Sort fields by order after population - FIXED VERSION
    this.sortFieldsByOrder();
    
    // Force change detection to update UI
    this.cdr.detectChanges();
    
    // Final debug after form population
  }
  
  // Helper method to determine if a field is default by name
  isDefaultFieldByName(fieldName: string): boolean {
    if (!fieldName) return false;
    const normalizedName = fieldName.toLowerCase().trim();
    return ['name', 'full name', 'email', 'phone'].includes(normalizedName);
  }
  
  // Helper method to determine if a field is a lead field by name or key
  isLeadFieldByName(fieldName: string): boolean {
    if (!fieldName) return false;
    const normalizedName = fieldName.toLowerCase().trim();
    return this.leadFields.some(field => 
      field.fieldName.toLowerCase() === normalizedName ||
      (field.key && field.key.toLowerCase() === normalizedName)
    );
  }

  // Helper method to determine if a field is a lead field by key
  isLeadFieldByKey(key: string): boolean {
    if (!key) return false;
    return this.leadFields.some(field => field.key === key);
  }

  get fields() {
    return this.meetingForm.get('fields') as FormArray;
  }

  addFieldWithKey(
    fieldName: string = '',
    fieldType: string = 'text',
    isRequired: boolean = false,
    isDefault: boolean = false,
    isLeadField: boolean = false,
    showInScheduler: boolean = true,
    options: string = '',
    prefillValue: string = '',
    order: number = 0,
    key: string | null = null
  ): void {
    // Determine initial isRequired state for lead fields
    const initialIsRequired = isLeadField && !showInScheduler ? false : isRequired;
    
    const newField = this.fb.group({
      fieldName: [fieldName, Validators.required],
      fieldType: [fieldType],
      isRequired: [initialIsRequired],
      isDefault: [isDefault],
      isLeadField: [isLeadField],
      showInScheduler: [showInScheduler],
      options: [options],
      prefillValue: [prefillValue],
      showPrefill: [this.showPrefillInputs && isDefault],
      isNew: [!isDefault && !isLeadField && !this.isEditMode],
      order: [order],
      key: [key]
    });
    
    // Set up the validation logic for this field
    this.setupFieldValidationLogic(newField);
    
    // If it's a lead field and showInScheduler is false, disable isRequired immediately
    if (isLeadField && !showInScheduler && this.meetingForm.get('createLeadMode')?.value === 'lead-and-meeting') {
      newField.get('isRequired')?.disable();
    }
    this.fields.push(newField);
  }

  addField(
    fieldName: string = '',
    fieldType: string = 'text',
    isRequired: boolean = false,
    isDefault: boolean = false,
    isLeadField: boolean = false,
    showInScheduler: boolean = true,
    options: string = '',
    prefillValue: string = '',
    order: number = 0
  ): void {
    // For backward compatibility, call addFieldWithKey with null key
    this.addFieldWithKey(
      fieldName,
      fieldType,
      isRequired,
      isDefault,
      isLeadField,
      showInScheduler,
      options,
      prefillValue,
      order,
      null
    );
  }

  addNewCustomField(): void {
    const nextOrder = this.getNextOrderValue();
    this.addField('', 'text', false, false, false, true, '', '', nextOrder);
  }

  removeField(index: number): void {
    // Check if the field is a default field
    const field = this.fields.at(index);
    if (field.get('isDefault')?.value === true) {
      // Just update the field properties instead of removing it
      field.patchValue({
        isRequired: false,
        showInScheduler: false
      });
    } else if (field.get('isLeadField')?.value === true) {
      // For lead fields, update visibility only
      field.patchValue({
        showInScheduler: false
      });
    } else {
      // Remove non-default/non-lead fields
      this.fields.removeAt(index);
      // Update order values after removal and then sort
      this.updateOrderValues();
      this.sortFieldsByOrder();
    }
  }

  // Drag and drop functionality
  onFieldDrop(event: CdkDragDrop<FormGroup[]>, fieldType: 'default' | 'lead' | 'custom'): void {
    const fields = this.getFieldsByType(fieldType);
    const fieldIndices = this.getFieldIndicesByType(fieldType);
    
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    
    // Move the items in the array
    moveItemInArray(fields, event.previousIndex, event.currentIndex);
    moveItemInArray(fieldIndices, event.previousIndex, event.currentIndex);
    
    // Update the form array order - FIXED VERSION
    this.reorderFormArray(fieldIndices);
    
    // Update order values in form controls
    this.updateOrderValues();
  }

  private getFieldsByType(fieldType: 'default' | 'lead' | 'custom'): FormGroup[] {
    switch (fieldType) {
      case 'default':
        return this.getDefaultFields();
      case 'lead':
        return this.getLeadFields();
      case 'custom':
        return this.getCustomFields();
      default:
        return [];
    }
  }

  private getFieldIndicesByType(fieldType: 'default' | 'lead' | 'custom'): number[] {
    const indices: number[] = [];
    this.fields.controls.forEach((control, index) => {
      const isDefault = control.get('isDefault')?.value;
      const isLeadField = control.get('isLeadField')?.value;
      
      switch (fieldType) {
        case 'default':
          if (isDefault) indices.push(index);
          break;
        case 'lead':
          if (isLeadField) indices.push(index);
          break;
        case 'custom':
          if (!isDefault && !isLeadField) indices.push(index);
          break;
      }
    });
    return indices;
  }

  // FIXED: Reorder form array without losing controls
  private reorderFormArray(newIndices: number[]): void {
    const fieldArray = this.meetingForm.get('fields') as FormArray;
    
    // ✅ Create a COPY of controls first, not a reference
    const reorderedControls = newIndices.map(index => fieldArray.at(index));
    
    // ✅ Use .clear() instead of while loop
    fieldArray.clear();
    
    // ✅ Re-append the copied controls
    reorderedControls.forEach(control => {
      fieldArray.push(control);
    });
  }

  private updateOrderValues(): void {
    this.fields.controls.forEach((control, index) => {
      control.get('order')?.setValue(index);
    });
  }

  private getNextOrderValue(): number {
    let maxOrder = -1;
    this.fields.controls.forEach(control => {
      const order = control.get('order')?.value || 0;
      if (order > maxOrder) {
        maxOrder = order;
      }
    });
    return maxOrder + 1;
  }

  // FIXED: Sort fields by order without losing controls
  private sortFieldsByOrder(): void {
    const fieldArray = this.meetingForm.get('fields') as FormArray;
    
    // ✅ Create a COPY of controls first, not a reference
    const sortedControls = [...fieldArray.controls].sort((a, b) => {
      const orderA = a.get('order')?.value ?? 0;
      const orderB = b.get('order')?.value ?? 0;
      return orderA - orderB;
    });
    
    // ✅ Use .clear() instead of while loop - keeps value/dirty flags intact
    fieldArray.clear();
    
    // ✅ Re-append the sorted controls
    sortedControls.forEach(control => {
      fieldArray.push(control);
    });
  }

  get f() {
    return this.meetingForm.controls;
  }
  
  // Check if a field is default by fieldName
  isDefaultField(fieldName: string): boolean {
    return this.defaultFields.some(field => field.fieldName === fieldName);
  }
  
  // Get only custom (non-default and non-lead) fields
  getCustomFields(): FormGroup[] {
    return this.fields.controls
      .filter(control => !control.get('isDefault')?.value && !control.get('isLeadField')?.value)
      .map(control => control as FormGroup);
  }
  
  // Get only default fields
  getDefaultFields(): FormGroup[] {
    return this.fields.controls
      .filter(control => control.get('isDefault')?.value)
      .map(control => control as FormGroup);
  }
  
  // Get only lead fields
  getLeadFields(): FormGroup[] {
    const leadFields = this.fields.controls
      .filter(control => {
        const isLeadField = control.get('isLeadField')?.value;
        // More lenient check - handle both boolean and string values
        return isLeadField === true || isLeadField === 'true';
      })
      .map(control => control as FormGroup);
    
    return leadFields;
  }

  // Helper method to mark all controls as touched
  markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        const formArray = control as FormArray;
        formArray.controls.forEach(ctrl => {
          if (ctrl instanceof FormGroup) {
            this.markFormGroupTouched(ctrl);
          } else {
            ctrl.markAsTouched();
          }
        });
      }
    });
  }

  private setupFieldValidationLogic(fieldControl: FormGroup): void {
    const showInSchedulerControl = fieldControl.get('showInScheduler');
    const isRequiredControl = fieldControl.get('isRequired');
    const isLeadFieldControl = fieldControl.get('isLeadField');
  
    if (showInSchedulerControl && isRequiredControl && isLeadFieldControl) {
      // Subscribe to showInScheduler changes
      showInSchedulerControl.valueChanges.subscribe(showInScheduler => {
        const isLeadField = isLeadFieldControl.value;
        const isLeadMode = this.meetingForm.get('createLeadMode')?.value === 'lead-and-meeting';
        
        // Only apply this logic for lead fields in lead-and-meeting mode
        if (isLeadField && isLeadMode) {
          if (!showInScheduler) {
            // If showInScheduler is false, set isRequired to false and disable it
            isRequiredControl.setValue(false);
            isRequiredControl.disable();
          } else {
            // If showInScheduler is true, enable isRequired
            isRequiredControl.enable();
          }
        }
      });
  
      // Also check when createLeadMode changes
      this.meetingForm.get('createLeadMode')?.valueChanges.subscribe(mode => {
        const isLeadField = isLeadFieldControl.value;
        const showInScheduler = showInSchedulerControl.value;
        
        if (isLeadField && mode === 'lead-and-meeting') {
          if (!showInScheduler) {
            isRequiredControl.setValue(false);
            isRequiredControl.disable();
          } else {
            isRequiredControl.enable();
          }
        } else if (mode === 'meeting-only') {
          // Re-enable isRequired when switching back to meeting-only mode
          isRequiredControl.enable();
        }
      });
    }
  }
}